import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';

import { useApiRequestControllable } from '@europrocurement/l2d-modules';
import {
    AchatsAppDispatch,
    FactureAchat,
    ReminderPublisher,
    useAchatsService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { ReminderPublisherApiRemoveFactureReminderPublisherItemRequest } from '@europrocurement/l2d-domain/openApi/ApiAchats';
import { getDataThunkType } from '@europrocurement/l2d-redux-utils';
import { MessagesTypes } from '@europrocurement/flexy-components/constants/wording/modals';

/**
 * Hook personnalisé pour gérer la dissociation d'une ou plusieurs factures d'un rappel fournisseur
 *
 * @returns Fonctions et état pour gérer la dissociation de factures
 */
const useDissociateInvoiceReminderPublisher = () => {
    const theme = useTheme();
    const xIdSociete = useSociete();
    const dispatch = useDispatch<AchatsAppDispatch>();
    const { reminderPublisherApi, selectReminderPublisher } = useAchatsService();
    const { request, requestState } = useApiRequestControllable();
    const { enqueueSnackbar } = useSnackbar();

    /**
     * Génère les messages de confirmation pour la dissociation multiple de factures
     *
     * @param facturesCount - Nombre de factures à dissocier
     * @returns Objets de messages pour la confirmation multiple
     */
    const getMultipleMessages = React.useCallback((facturesCount: number): MessagesTypes => {
        if (facturesCount === 0) return {};

        return {
            question: `Êtes-vous sûr de vouloir dissocier ces ${facturesCount} factures ?`,
            detail: {
                message: 'Vous pourrez les associer à nouveau plus tard.',
                severity: 'info',
            },
            validation: 'Oui, je veux dissocier ces factures',
            annulation: 'Non, je veux annuler',
        };
    }, []);

    /**
     * Dissocie une seule facture d'un rappel fournisseur
     *
     * @param reminderPublisher - Le rappel fournisseur concerné
     * @param factureAchat - La facture à dissocier
     * @param fetchInvoices - Fonction pour rafraîchir la liste des factures après dissociation
     */
    const dissociateInvoiceReminderPublisher = React.useCallback(
        (
            reminderPublisher: ReminderPublisher,
            factureAchat: FactureAchat,
            fetchInvoices: getDataThunkType<FactureAchat>,
        ) => {
            if (!reminderPublisher?.id || !factureAchat?.id) return;

            const requestParameters: ReminderPublisherApiRemoveFactureReminderPublisherItemRequest =
                {
                    id: reminderPublisher.id,
                    factureId: factureAchat.id.toString(),
                    xIdSociete,
                };

            request(
                () => reminderPublisherApi.removeFactureReminderPublisherItem(requestParameters),
                {
                    successMessage: 'Facture dissociée de ce rappel avec succès.',
                    errorMessage: 'Une erreur est survenue lors de la dissociation de la facture.',
                    successCallback() {
                        setTimeout(() => {
                            dispatch(fetchInvoices({}));
                            if (reminderPublisher?.id) {
                                dispatch(selectReminderPublisher({ id: reminderPublisher.id }));
                            }
                        }, 1500);
                    },
                    confirmationPromise: true,
                    confirmationPromiseProps: {
                        color: theme.palette.warning,
                        messages: {
                            question: 'Êtes-vous sûr de vouloir dissocier cette facture ?',
                            detail: {
                                message: "Vous pourrez l'associer à nouveau plus tard.",
                                severity: 'info',
                            },
                            validation: 'Oui, je veux dissocier cette facture',
                            annulation: 'Non, je veux annuler',
                        },
                        loadingMessage: 'Dissociation en cours...',
                    },
                },
            );
        },
        [
            dispatch,
            reminderPublisherApi,
            request,
            selectReminderPublisher,
            theme.palette.warning,
            xIdSociete,
        ],
    );

    /**
     * Dissocie plusieurs factures d'un même rappel fournisseur simultanément
     *
     * @param reminderPublisher - Le rappel fournisseur concerné
     * @param factures - Tableau de factures à dissocier
     * @param fetchInvoices - Fonction pour rafraîchir la liste des factures après dissociation
     */
    const dissociateMultipleInvoiceReminderPublisher = React.useCallback(
        async (
            reminderPublisher: ReminderPublisher,
            factures: FactureAchat[],
            fetchInvoices: getDataThunkType<FactureAchat>,
        ) => {
            if (!reminderPublisher?.id || factures.length === 0) return;

            // Filtrer les factures valides (avec ID)
            const validInvoices = factures.filter((facture) => !!facture.id);

            if (validInvoices.length === 0) {
                enqueueSnackbar('Aucune facture valide à dissocier', { variant: 'warning' });
                return;
            }

            // Demander confirmation à l'utilisateur
            try {
                await request(
                    // On utilise seulement la méthode GET pour montrer la modal de confirmation
                    // sans effectuer réellement l'action
                    () =>
                        reminderPublisherApi.getReminderPublisherItem({
                            id: reminderPublisher.id!,
                            xIdSociete,
                        }),
                    {
                        confirmationPromise: true,
                        withToaster: false, // Ne pas afficher de toast pour cette requête de confirmation
                        confirmationPromiseProps: {
                            color: theme.palette.warning,
                            messages: getMultipleMessages(validInvoices.length),
                            loadingMessage: 'Préparation de la dissociation multiple...',
                        },
                    },
                );

                // Si l'utilisateur confirme, traiter la dissociation en masse
                enqueueSnackbar(`Début de la dissociation de ${validInvoices.length} factures...`, {
                    variant: 'info',
                });

                // Créer un tableau de promesses de dissociation
                const dissociatePromises = validInvoices.map(async (facture) => {
                    try {
                        if (!facture.id || !reminderPublisher.id) return false;

                        const requestParameters: ReminderPublisherApiRemoveFactureReminderPublisherItemRequest =
                            {
                                id: reminderPublisher.id,
                                factureId: facture.id.toString(),
                                xIdSociete,
                            };

                        await reminderPublisherApi.removeFactureReminderPublisherItem(
                            requestParameters,
                        );
                        return true;
                    } catch (error) {
                        console.error(
                            `Erreur lors de la dissociation de la facture ${facture.id}:`,
                            error,
                        );
                        return false;
                    }
                });

                // Exécuter les dissociations et attendre les résultats
                const results = await Promise.all(dissociatePromises);

                // Compter les succès et échecs
                const successCount = results.filter(Boolean).length;
                const failureCount = validInvoices.length - successCount;

                // Afficher un message récapitulatif
                if (failureCount === 0) {
                    enqueueSnackbar(
                        `Toutes les factures (${successCount}) ont été dissociées avec succès.`,
                        { variant: 'success' },
                    );
                } else if (successCount === 0) {
                    enqueueSnackbar(`Échec de la dissociation de toutes les factures.`, {
                        variant: 'error',
                    });
                } else {
                    enqueueSnackbar(
                        `${successCount} factures dissociées avec succès, ${failureCount} échecs.`,
                        { variant: 'warning' },
                    );
                }

                setTimeout(() => {
                    dispatch(fetchInvoices({}));
                    if (reminderPublisher?.id) {
                        dispatch(selectReminderPublisher({ id: reminderPublisher.id }));
                    }
                }, 1500);
            } catch (error) {
                // L'utilisateur a annulé ou une erreur est survenue au niveau de la confirmation
                console.error('Opération multiple annulée ou erreur:', error);
            }
        },
        [
            dispatch,
            enqueueSnackbar,
            getMultipleMessages,
            reminderPublisherApi,
            request,
            selectReminderPublisher,
            theme.palette.warning,
            xIdSociete,
        ],
    );

    return {
        dissociateInvoiceReminderPublisher,
        dissociateMultipleInvoiceReminderPublisher,
        state: requestState,
    };
};

export default useDissociateInvoiceReminderPublisher;
